import { useEffect, useRef, useState } from 'react';
import ChevroneTop from '@/assets/icons/chevrone-top.svg';
import type { FullPlan } from '@/types/FullPlan';
import Icon from '../Icon/Icon';
import CheckIcon from '@/assets/icons/CheckIcon.svg';
import Board from '@/assets/icons/Board.svg';
import Image from 'next/image';
import { formatCurrencyWithCommas } from '@/utils/formatNumber';

interface PlanSummaryProps {
    plan: FullPlan;
    isSub: boolean;
    toggleSub: () => void;
    familyAges?: string[];
    /** When true, the desktop "Show Details" control is omitted (e.g. details are forced open elsewhere). */
    hideDetailsToggle?: boolean;
}

export const PlanSummary = ({ plan, isSub, toggleSub, familyAges = [], hideDetailsToggle = false }: PlanSummaryProps) => {
    const isFamilyPlan = familyAges.length > 1;
    const rowRef = useRef<HTMLDivElement | null>(null);
    const showDetailsRef = useRef<HTMLDivElement | null>(null);
    const [isShowDetailsWrapped, setIsShowDetailsWrapped] = useState(false);
    
    const deductible = isFamilyPlan 
        ? parseFloat(plan.family_medical_deductible?.replace(/[^0-9.]/g, '') || '0')
        : plan.deductible_numerical;
    
    const maxOutOfPocket = isFamilyPlan
        ? parseFloat(plan.family_medical_moop?.replace(/[^0-9.]/g, '') || '0')
        : plan.max_out_of_pocket_numerical;

    useEffect(() => {
        const isDesktop = () => {
            if (typeof window === 'undefined') return false;
            // Tailwind `lg` starts at 1024px
            return window.matchMedia('(min-width: 1024px)').matches;
        };

        const measure = () => {
            if (!isDesktop()) {
                setIsShowDetailsWrapped(false);
                return;
            }
            const rowEl = rowRef.current;
            const btnEl = showDetailsRef.current;
            if (!rowEl || !btnEl) return;

            const rowRect = rowEl.getBoundingClientRect();
            const btnRect = btnEl.getBoundingClientRect();
            // If the button's right edge exceeds the row's right edge, it is getting clipped/overflowing.
            const overflowed = btnRect.right > rowRect.right + 1;
            setIsShowDetailsWrapped(overflowed);
        };

        // Measure after layout (and on resize/zoom).
        const raf = window.requestAnimationFrame(measure);
        window.addEventListener('resize', measure);
        return () => {
            window.cancelAnimationFrame(raf);
            window.removeEventListener('resize', measure);
        };
    }, [plan.display_name, plan.plan_type, isSub, familyAges.length]);

    return (
        <div className="mb-1 w-full overflow-hidden">
            <div
                ref={rowRef}
                className="flex flex-col md:flex-row lg:flex-row lg:flex-wrap items-start gap-3 sm:gap-2 md:gap-4 md:divide-x md:divide-blue-100 w-full overflow-hidden"
            >
                <div className="flex md:hidden w-full flex-col md:pr-4 md:shrink-0 min-w-0 overflow-hidden">
                    <div className="text-sm sm:text-base font-normal text-gray-500">Plan Name</div>
                    <div className="text-base sm:text-base font-medium text-gray-900 wrap-break-word">{plan.display_name}</div>
                </div>
                {/* Mobile: Type, Deductible, Max-Out-Of-Pocket in one row */}
                <div className="flex md:hidden justify-between flex-row w-full gap-2">
                    <div className="flex flex-col shrink-0 min-w-0 overflow-hidden">
                        <div className="text-sm sm:text-base font-normal text-gray-500">Type</div>
                        <div className="text-base sm:text-base font-medium text-gray-900 wrap-break-word">{plan.plan_type}</div>
                    </div>
                    <div className="flex flex-col shrink-0 min-w-0 overflow-hidden">
                        <div className="text-sm sm:text-base font-normal text-gray-500">Deductible</div>
                        <div className="text-base sm:text-base font-medium text-gray-900 wrap-break-word">{formatCurrencyWithCommas(deductible)}/yr</div>
                    </div>
                    <div className="flex flex-col shrink-0 min-w-0 overflow-hidden">
                        <div className="text-sm sm:text-base font-normal text-gray-500">Max–Out–Of–Pocket</div>
                        <div className="text-base sm:text-base font-medium text-gray-900 wrap-break-word">{formatCurrencyWithCommas(maxOutOfPocket)}/yr</div>
                    </div>
                </div>
                {/* Tablet and Desktop: Type, Deductible, Max-Out-Of-Pocket as separate columns */}
                <div className="hidden md:flex flex-col md:w-[15%] lg:w-[15%] md:flex-none lg:flex-none md:pr-2 lg:pr-4 shrink-0 min-w-0 overflow-hidden">
                    <div className="text-sm sm:text-base font-normal text-gray-500">Type</div>
                    <div className="text-base sm:text-base font-medium text-gray-900 wrap-break-word">{plan.plan_type}</div>
                </div>
                <div className="hidden md:flex flex-col md:flex-1 lg:flex-none md:w-auto lg:w-auto md:pr-2 lg:pr-4 shrink-0 min-w-0 overflow-hidden">
                    <div className="text-sm sm:text-base font-normal text-gray-500">Deductible</div>
                    <div className="text-base sm:text-base font-medium text-gray-900 wrap-break-word">{formatCurrencyWithCommas(deductible)}/yr</div>
                </div>
                <div className="hidden md:flex flex-col md:flex-1 lg:flex-none md:w-auto lg:w-auto md:pr-4 lg:pr-4 shrink-0 min-w-0">
                    <div className="text-sm sm:text-base font-normal text-gray-500 whitespace-nowrap">Max–Out–Of–Pocket</div>
                    <div className="text-base sm:text-base font-medium text-gray-900 wrap-break-word">{formatCurrencyWithCommas(maxOutOfPocket)}/yr</div>
                </div>

                {/* Desktop (lg+): HSA Eligible and Specialist Without Referral in same row */}
                <div className="hidden lg:flex lg:w-auto flex-row lg:flex-col gap-2 lg:gap-1 lg:pr-4 lg:shrink-0 min-w-0 overflow-hidden">
                    {plan.hsa_eligible && (
                        <div className="flex items-center gap-1 text-base sm:text-base text-gray-500">
                            <Image src={CheckIcon} alt="" width={20} height={20} />
                            <span className="text-sm md:text-base">HSA Eligible</span>
                        </div>
                    )}
                    <div className="flex items-center gap-1 text-base sm:text-base text-gray-500">
                        {!plan.gated ? (
                            <Image src={CheckIcon} alt="" width={20} height={20} />
                        ) : (
                            <Image src={Board} alt="Board" width={16} height={16} />
                        )}
                        <span 
                            className="text-sm md:text-base"
                            style={plan.gated ? { 
                                textDecoration: 'line-through',
                                textDecorationThickness: '1px',
                                textDecorationColor: '#6B7280'
                            } : {}}
                        >
                            Specialist Without Referral
                        </span>
                    </div>
                </div>

                {!hideDetailsToggle && (
                <div
                    className={`hidden lg:flex flex-col items-center justify-center gap-1 self-center ${
                        isShowDetailsWrapped ? 'lg:basis-full lg:justify-center lg:mt-2' : 'lg:w-auto lg:ml-auto shrink-0'
                    }`}
                >
                    <div
                        ref={showDetailsRef}
                        className="flex cursor-pointer gap-1 text-base text-primary hover:underline whitespace-nowrap w-auto justify-start"
                        onClick={toggleSub}
                    >
                        {isSub ? 'Hide Details' : 'Show Details'}{' '}
                        <Icon
                            src={ChevroneTop}
                            className={`${isSub ? `rotate-0` : 'rotate-180'} transition-all duration-200`}
                        />
                    </div>
                </div>
                )}
            </div>
            {/* Tablet (md, not lg): HSA Eligible and Specialist Without Referral in one row */}
            <div className="hidden md:flex lg:hidden w-full flex-row flex-wrap items-center gap-4 mt-3">
                {plan.hsa_eligible && (
                    <div className="flex items-center gap-1 text-base sm:text-base text-gray-500">
                        <Image src={CheckIcon} alt="" width={20} height={20} />
                        <span className="text-base sm:text-base">HSA Eligible</span>
                    </div>
                )}
                <div className="flex items-center gap-1 text-base sm:text-base text-gray-500">
                    {!plan.gated ? (
                        <Image src={CheckIcon} alt="" width={20} height={20} />
                    ) : (
                        <Image src={Board} alt="Board" width={16} height={16} />
                    )}
                    <span 
                        className="text-base sm:text-base"
                        style={plan.gated ? { 
                            textDecoration: 'line-through',
                            textDecorationThickness: '1px',
                            textDecorationColor: '#6B7280'
                        } : {}}
                    >
                        Specialist Without Referral
                    </span>
                </div>
            </div>
            {/* Mobile: HSA Eligible and Specialist Without Referral */}
            <div className="flex md:hidden w-full flex-row gap-2 mt-3">
                {plan.hsa_eligible && (
                    <div className="flex items-center gap-1 text-sm md:text-base text-gray-500">
                        <Image src={CheckIcon} alt="" width={20} height={20} />
                        <span className="text-sm md:text-base">HSA Eligible</span>
                    </div>
                )}
                <div className="flex items-center gap-1 text-sm md:text-base text-gray-500">
                    {!plan.gated ? (
                        <Image src={CheckIcon} alt="" width={20} height={20} />
                    ) : (
                        <Image src={Board} alt="Board" width={16} height={16} />
                    )}
                    <span 
                        className="text-sm md:text-base"
                        style={plan.gated ? { 
                            textDecoration: 'line-through',
                            textDecorationThickness: '1px',
                            textDecorationColor: '#6B7280'
                        } : {}}
                    >
                        Specialist Without Referral
                    </span>
                </div>
            </div>
        </div>
    );
};
